import { Option } from '../models/option';
import { ConversionFormula } from '../models/set-point';

// Auto-Clear Alarm Values
export enum AutoClearValue {
  Off = '',
  Acked = 'acked',
  All = 'all'
}

export const AUTO_CLEAR_VALUES: readonly string[] = [
  AutoClearValue.Off,
  AutoClearValue.Acked,
  AutoClearValue.All,
];

/**
 * Single source of truth for tile configuration options
 * Used in: tile editor, CSV validation, tile glossary
 */

// Decimal Places
export const DECIMAL_OPTIONS = [0, 1, 2, 3, 4, 5, 6] as const;
export const MIN_DECIMAL_PLACES = 0;
export const MAX_DECIMAL_PLACES = 6;

// Alarm Delay (in seconds)
export const MIN_ALARM_DELAY = 0;
export const MAX_ALARM_DELAY = 86400; // 24 hours

export enum CompareOperator {
  Equal = '=',
  GreaterThan = '>',
  GreaterThanOrEqual = '>=',
  LessThan = '<',
  LessThanOrEqual = '<='
}

// Grouped subsets for validation/UI
export const HIGH_ALARM_OPERATORS = [
  CompareOperator.GreaterThan,
  CompareOperator.GreaterThanOrEqual
] as const;

export const LOW_ALARM_OPERATORS = [
  CompareOperator.LessThan,
  CompareOperator.LessThanOrEqual
] as const;

export const ALARM_OPERATOR_VALUES = [
  ...HIGH_ALARM_OPERATORS,
  ...LOW_ALARM_OPERATORS
] as const;

export type AnalogAlarmOperator = typeof ALARM_OPERATOR_VALUES[number];

// Conversion Formulas
export const CONVERSION_OPTIONS: Option[] = [
  { id: ConversionFormula.None, name: 'None' },
  { id: ConversionFormula.CelsiusToFahrenheit, name: '°C to °F' },
  { id: ConversionFormula.FahrenheitToCelsius, name: '°F to °C' },
  { id: ConversionFormula.KiloPascalToPSI, name: 'kPa to psi' },
  { id: ConversionFormula.PSIToKiloPascal, name: 'psi to kPa' },
  { id: ConversionFormula.LitersPerSecondToCFM, name: 'l/s to CFM' },
  { id: ConversionFormula.CFMToLitersPerSecond, name: 'CFM to l/s' }
];

export const CONVERSION_VALUES: readonly ConversionFormula[] = [
  ConversionFormula.None,
  ConversionFormula.CelsiusToFahrenheit,
  ConversionFormula.FahrenheitToCelsius,
  ConversionFormula.KiloPascalToPSI,
  ConversionFormula.PSIToKiloPascal,
  ConversionFormula.LitersPerSecondToCFM,
  ConversionFormula.CFMToLitersPerSecond
];

// Multiplier Options
export const MULTIPLIER_OPTIONS: Option[] = [
  { id: '10000', name: '10,000' },
  { id: '1000', name: '1,000' },
  { id: '100', name: '100' },
  { id: '10', name: '10' },
  { id: '1', name: '1' },
  { id: '0.1', name: '.1' },
  { id: '0.01', name: '.01' },
  { id: '0.001', name: '.001' },
  { id: '0.0001', name: '.0001' }
];

export const DEFAULT_STALE_THRESHOLD_MINUTES = 10;
export const DATA_AGE_REFRESH_INTERVAL_MS = 5_000;
export const FORMULA_STALENESS_REFRESH_INTERVAL_MS = 60_000;
export const MULTIPLIER_VALUES = [
  '',
  '0.0001',
  '0.001',
  '0.01',
  '0.1',
  '1',
  '10',
  '100',
  '1000',
  '10000'
] as const;
